import type React from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import { useQuery } from '@apollo/client';

import GenericPage from 'app/ui/components/layouts/generic-page';
import ErrorComponent from 'app/ui/components/molecules/error';
import Loading from 'app/ui/components/molecules/loading';
import ResourceHeader from 'app/ui/components/molecules/resource-header';
import GradientActionCard from 'app/ui/components/molecules/gradient-action-card';
import Breadcrumb from 'app/ui/components/molecules/breadcrumb';
import Grid from 'app/ui/components/molecules/grid';
import { routes } from 'app/routes/constants';

import { TrainingPlans as TRAINING_PLANS } from 'app/pages/training-plans/query.gql';
import type { TrainingPlansQuery, TrainingPlansQueryVariables } from 'app/types/graphql';

const BreadcrumbWrapper = styled.div`
  display: flex;
  margin-top: ${({ theme: { spacing } }) => spacing(10)};
  margin-bottom: -${({ theme: { spacing } }) => spacing(2)};
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme: { spacing } }) => spacing(2)};
`;

const StyledResourceHeader = styled(ResourceHeader)`
  margin-bottom: ${({ theme }) => theme.spacing(8)};

  ${({ theme: { media } }) => `${media.medium(() => 'margin-bottom: 0;')}`};
`;

const TrainingPlansPage: React.FC = () => {
  const intl = useIntl();

  const { data, loading, error } = useQuery<TrainingPlansQuery, TrainingPlansQueryVariables>(TRAINING_PLANS);

  if (loading) {
    return <Loading />;
  }

  if (error || !data) {
    return <ErrorComponent />;
  }

  const plans = data.allProgressivePlans?.nodes || [];
  const pageTitle = intl.formatMessage({ id: 'library.trainingPlans' });

  return (
    <GenericPage pageTitle={pageTitle}>
      <StyledResourceHeader
        title={pageTitle}
        content={intl.formatMessage({ id: 'library.trainingPlansCopy' })}
        imageUrl="/plans-bg.png"
      />
      <BreadcrumbWrapper>
        <Breadcrumb contentTitle={pageTitle} />
      </BreadcrumbWrapper>
      <ContentWrapper>
        <Grid elementsPerRow={2}>
          {plans.map((plan) => (
            <GradientActionCard
              key={plan.id}
              title={plan.name}
              shortDescription={plan.longDescription || ''}
              ctaImageUrl={plan.ctaImage?.signedUrl || plan.ctaImage?.url}
              to={routes.TRAINING_PLAN.replace(':id', plan.id)}
              size="large"
            />
          ))}
        </Grid>
      </ContentWrapper>
    </GenericPage>
  );
};

export default TrainingPlansPage;
